import * as _ from 'lodash';
import { useState } from 'react';
import { useDispatch } from 'react-redux';

import { Action, type Permission, ResourceType } from '../../../../typings/permissions';
import { useAddToast } from '../../../contexts/ToastContext';
import {
  useOrganizationPackageLabels,
  usePackageLabelAssignments,
  useUpdatePackageLabelAssignments,
} from '../../../hooks/api/packageLabels';
import queryClient from '../../../ReactQuery/client';
import { getErrorMessage } from '../../../ReactQuery/errors';
import * as DependenciesV2Store from '../../../redux/dependenciesV2';
import useHasPermission from '../../../shared/permissions/useHasPermission';
import { useTabs } from '../../generic';

import { Tab, type VersionedLabelOption } from './types';
import { reduceAssignmentsToVersionMap } from './utils';
import type { SinglePackageLabelState } from './packageLabelStateTypes';

/**
 * overall state hook for the package label modal encompassing all tabs and updates
 */
export const usePackageLabelState = (packageId: string, parentProjectLocator: string): SinglePackageLabelState => {
  const tabs = useTabs({ tabIds: Object.values(Tab), defaultTab: Tab.Global });
  const addToast = useAddToast();
  const hasPermission = useHasPermission();
  const dispatch = useDispatch();

  /**
   * The user must have global permissions to edit global scoped labels.
   * If the user has global OR local permissions, then they can edit project and revision scoped labels.
   *
   */
  const globalPermission: Permission = { action: Action.Set, resourceType: ResourceType.PackageLabel };
  const localPermission: Permission = {
    action: Action.SetLocal,
    resourceType: ResourceType.ProjectPackageLabel,
    projectId: parentProjectLocator,
  };
  const hasGlobalPermission = hasPermission(globalPermission);
  const hasLocalPermission = hasPermission(localPermission);

  const hasNoPermission: boolean = !hasGlobalPermission && !hasLocalPermission;

  // init API hooks
  const { data: labels, isLoading: isLoadingLabels } = useOrganizationPackageLabels();
  const { data: labelAssignments, isLoading: isLoadingLabelAssignments } = usePackageLabelAssignments(packageId);
  const { updatePackageLabelAssignments } = useUpdatePackageLabelAssignments(packageId);

  // set up state for selected labels in each scope
  const [globalLabelsSelected, setGlobalLabelsSelected] = useState<VersionedLabelOption>({});
  const [projectLabelsSelected, setProjectLabelsSelected] = useState<VersionedLabelOption>({});
  const [revisionLabelsSelected, setRevisionLabelsSelected] = useState<VersionedLabelOption>({});

  // helper functions to get the initial assignments for each scope before any changes are made
  const getInitialGlobalAssignments = () => {
    return (
      labelAssignments?.packageLabelAssignments
        .filter((assignment) => assignment.scope === 'org')
        .reduce(reduceAssignmentsToVersionMap, {}) || {}
    );
  };

  const getInitialProjectAssignments = (projectLocator: string) => {
    return (
      labelAssignments?.packageLabelAssignments
        .filter((assignment) => assignment.scope === 'project' && assignment.scopeId === projectLocator)
        .reduce(reduceAssignmentsToVersionMap, {}) || {}
    );
  };

  const getInitialRevisionAssignments = (parentRevisionLocator: string) => {
    return (
      labelAssignments?.packageLabelAssignments
        .filter((assignment) => assignment.scope === 'revision' && assignment.scopeId === parentRevisionLocator)
        .reduce(reduceAssignmentsToVersionMap, {}) || {}
    );
  };

  const initializeGlobalSelected = () => {
    setGlobalLabelsSelected(getInitialGlobalAssignments());
  };
  const initializeProjectSelected = (projectLocator: string) => {
    setProjectLabelsSelected(getInitialProjectAssignments(projectLocator));
  };
  const initializeRevisionSelected = (parentRevisionLocator: string) => {
    setRevisionLabelsSelected(getInitialRevisionAssignments(parentRevisionLocator));
  };

  const onSaveSuccess = () => {
    addToast({ type: 'success', content: 'Labels updated successfully' });
    queryClient.invalidateQueries({ queryKey: ['packageLabels'] });
    dispatch(DependenciesV2Store.actions.preFetchLoading());
    dispatch(DependenciesV2Store.actions.fetchDependencies());
  };

  // save functions for each scope
  //  Returns a promise, so that we can do things like close the modal afterwards
  const onSaveGlobal = async () => {
    updatePackageLabelAssignments.mutateAsync(
      {
        newLabelIds: globalLabelsSelected,
        scope: 'org',
      },
      {
        onSuccess() {
          onSaveSuccess();
          initializeGlobalSelected();
        },
        onError(error) {
          addToast({ type: 'error', content: `Error updating global labels: ${getErrorMessage(error)}` });
        },
      },
    );
  };

  //  Returns a promise, so that we can do things like close the modal afterwards
  const onSaveProject = async (projectLocator: string) => {
    updatePackageLabelAssignments.mutateAsync(
      {
        newLabelIds: projectLabelsSelected,
        scope: 'project',
        scopeId: projectLocator,
      },
      {
        onSuccess() {
          onSaveSuccess();
          initializeProjectSelected(projectLocator);
        },
        onError(error) {
          addToast({ type: 'error', content: `Error updating project labels: ${getErrorMessage(error)}` });
        },
      },
    );
  };

  //  Returns a promise, so that we can do things like close the modal afterwards
  const onSaveRevision = async (parentRevisionLocator: string) => {
    updatePackageLabelAssignments.mutateAsync(
      {
        newLabelIds: revisionLabelsSelected,
        scope: 'revision',
        scopeId: parentRevisionLocator,
      },
      {
        onSuccess() {
          onSaveSuccess();
          initializeRevisionSelected(parentRevisionLocator);
        },
        onError(error) {
          addToast({ type: 'error', content: `Error updating revision labels: ${getErrorMessage(error)}` });
        },
      },
    );
  };

  // Keep track of which tabs are "dirty" meaning that their current state
  // differs from their initial state.
  const getDirtyTabs = (parentRevisionLocator: string): Record<Tab, boolean> => ({
    [Tab.Global]: !_.isEqual(globalLabelsSelected, getInitialGlobalAssignments()),
    [Tab.Project]: !_.isEqual(projectLabelsSelected, getInitialProjectAssignments(parentProjectLocator)),
    [Tab.Revision]: !_.isEqual(
      revisionLabelsSelected,
      getInitialRevisionAssignments(parentRevisionLocator ?? ''),
    ),
  });
  const getIsAnyTabDirty = (parentRevisionLocator: string): boolean =>
    Object.values(getDirtyTabs(parentRevisionLocator)).some((isDirty) => isDirty);

  return {
    mode: 'single' as const,
    tabs,
    getInitialGlobalAssignments,
    globalLabelsSelected,
    setGlobalLabelsSelected,
    initializeGlobalSelected,
    getInitialProjectAssignments,
    projectLabelsSelected,
    setProjectLabelsSelected,
    initializeProjectSelected,
    getInitialRevisionAssignments,
    revisionLabelsSelected,
    setRevisionLabelsSelected,
    initializeRevisionSelected,
    labels,
    isLoadingLabels,
    labelAssignments,
    isLoadingLabelAssignments,
    onSaveGlobal,
    onSaveProject,
    onSaveRevision,
    hasGlobalPermission,
    hasLocalPermission,
    hasNoPermission,
    getDirtyTabs,
    getIsAnyTabDirty,
  };
};
